import type { HttpErrorResponse } from '@angular/common/http';

import type { CorrelationId, PickActions } from '@core/ngrx-legacy';
import { createEntityCollectionStore, pipeState } from '@core/ngrx-legacy';
import type { Stage, StageGroup } from '@core/stages-types';

import type { PipelineOfferSavePayload } from '../../../services/pipeline-template-offer-http-client';

export interface MovePayload {
  offerId: number;
  stageId: number;
  group: StageGroup;
  placeAfterStageId?: number;
}

export interface UpdatePositionsPayload {
  sourceTemplateId: number;
  offerId: number;
  stagesIds: number[];
}

const idSelector = ({ id }: { id: number }) => id;
const { actions, reducer } = createEntityCollectionStore<Stage>()('PipelineTemplateOfferStage', {
  idSelector,

  customActions: adapter => ({
    createOnePessimistic: (
      state,
      payload: { data: PipelineOfferSavePayload } & Required<CorrelationId>,
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.correlationId] }),

    updateOnePessimistic: (state, payload: { data: PipelineOfferSavePayload }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.data.sourceTemplateId!] }),

    removeOnePessimistic: (
      state,
      payload: {
        offerId: number;
        sourceTemplateId: number;
        stageId: number;
        destinationStageId?: number;
      },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
    removeOnePessimisticSuccess: (state, payload: { data: Stage; removedStageId: number }) =>
      adapter.removeOne(state, payload.data.id),

    move: (state, payload: MovePayload) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
    moveSuccess: (state, payload: { data: Stage; offerId: number }) =>
      adapter.upsertOne(state, payload.data),
    moveFailed: (state, payload: { error: HttpErrorResponse; data: MovePayload }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.data.stageId] }),

    updatePositions: (state, payload: UpdatePositionsPayload) =>
      adapter.markAsPending(state, { scope: null, entities: payload.stagesIds }),
    updatePositionsSuccess: (state, payload: { data: Stage[]; pipelineTemplateId: number }) =>
      pipeState(
        state,
        next => adapter.updateMany(next, payload.data),
        next => adapter.markAsLoaded(next, { scope: null, entities: payload.data.map(idSelector) }),
      ),
    updatePositionsFailed: (
      state,
      payload: { error: HttpErrorResponse; data: UpdatePositionsPayload },
    ) => adapter.setError(state, payload.error, { scope: null, entities: payload.data.stagesIds }),
  }),
});

const PipelineTemplateOfferStageActions: PickActions<
  typeof actions,
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
  | 'updatePositions'
  | 'move'
> = actions;

export { PipelineTemplateOfferStageActions, reducer as PipelineTemplateOfferStageReducer };
